import React, { useState, useEffect } from 'react';
import { XMarkIcon, ArrowDownTrayIcon, DocumentIcon } from '@heroicons/react/24/outline';
import { learningAPI } from '../../services/api';

interface PDFViewerProps {
  isOpen: boolean;
  onClose: () => void;
  content: {
    id: string;
    title: string;
    fileUrl: string;
    fileName?: string;
    section?: {
      name: string;
      team: {
        name: string;
      };
    };
    createdAt: string;
  };
}

const PDFViewer: React.FC<PDFViewerProps> = ({ isOpen, onClose, content }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string>('');

  useEffect(() => {
    if (isOpen && content.id) {
      setIsLoading(true);
      setError(null);
      learningAPI.getFileBlob(content.id)
        .then(url => {
          setPdfUrl(url);
          setIsLoading(false);
        })
        .catch(err => {
          console.error('Error loading PDF:', err);
          setError(err.message || 'Failed to load PDF. Please try downloading the file instead.');
          setIsLoading(false);
        });
    }

    // Cleanup blob URL when component unmounts or content changes
    return () => {
      if (pdfUrl) {
        window.URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [isOpen, content.id]);

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setError('Failed to load PDF. Please try downloading the file instead.');
  };

  const handleDownload = async () => {
    try {
      await learningAPI.downloadFile(content.id, content.fileName || `${content.title}.pdf`);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
          {/* Header */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <DocumentIcon className="h-6 w-6 text-red-600" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {content.title}
                  </h3>
                  {content.section && (
                    <p className="text-sm text-gray-500">
                      {content.section.name} • {content.section.team.name}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleDownload}
                  className="bg-primary-600 text-white rounded-md p-2 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  title="Download PDF"
                >
                  <ArrowDownTrayIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={onClose}
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>

          {/* PDF Viewer */}
          <div className="px-4 pb-4 sm:px-6 sm:pb-6">
            <div className="relative w-full" style={{ height: '70vh' }}>
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg">
                  <div className="flex flex-col items-center space-y-2">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                    <p className="text-sm text-gray-500">Loading PDF...</p>
                  </div>
                </div>
              )}

              {error ? (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg">
                  <div className="text-center p-6">
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                      <XMarkIcon className="h-6 w-6 text-red-600" />
                    </div>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">PDF Loading Error</h3>
                    <p className="mt-1 text-sm text-gray-500">{error}</p>
                    <div className="mt-4">
                      <button
                        onClick={handleDownload}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                        Download PDF
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <iframe
                  className="absolute top-0 left-0 w-full h-full rounded-lg border"
                  src={pdfUrl ? `${pdfUrl}#toolbar=1&navpanes=1&scrollbar=1` : ''}
                  title={content.title}
                  onLoad={handleIframeLoad}
                  onError={handleIframeError}
                />
              )}
            </div>

            {/* File Info */}
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between text-sm text-gray-500">
                <div className="flex items-center space-x-4">
                  <span>Added on {new Date(content.createdAt).toLocaleDateString()}</span>
                  {content.fileName && (
                    <span>File: {content.fileName}</span>
                  )}
                </div>
                <button
                  onClick={handleDownload}
                  className="text-primary-600 hover:text-primary-700 font-medium flex items-center space-x-1"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  <span>Download</span>
                </button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDFViewer;
